import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';

export interface MultiSelectOption<T extends string | number> {
  value: T;
  label: string;
  sublabel?: string;
}

interface CheckboxMultiSelectProps<T extends string | number> {
  options: MultiSelectOption<T>[];
  selected: T[];
  onChange: (selected: T[]) => void;
  searchable?: boolean;
  emptyText?: string;
  maxHeightClass?: string;
  /** Optional extra control rendered at the end of each row (e.g. a "view details" button). */
  renderRowEnd?: (option: MultiSelectOption<T>) => ReactNode;
  /** The search box's placeholder key; the flags page's by default. */
  searchPlaceholder?: string;
  /**
   * Draw at most this many rows; the search narrows the rest. Thousands of checkboxes took the
   * picker a long moment to open (4,211 projects). Ticked rows come first and are always drawn.
   */
  maxRendered?: number;
  /** Leave out the «N נבחרו» line, where the count is already shown beside the list. */
  hideCount?: boolean;
  /** The options are still on their way: a spinner instead of «אין תוצאות». */
  loading?: boolean;
}

/** Lightweight searchable checkbox list for picking funds / users / groups / classifications. */
function CheckboxMultiSelect<T extends string | number>({
  options,
  selected,
  onChange,
  searchable = true,
  emptyText,
  maxHeightClass = 'max-h-56',
  renderRowEnd,
  searchPlaceholder = 'featureFlags.search',
  maxRendered,
  hideCount = false,
  loading = false,
}: CheckboxMultiSelectProps<T>) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const selectedSet = useMemo(() => new Set(selected.map(String)), [selected]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    // Labels come straight from data (a fund's name, a user's email), and one null among
    // thousands took the whole screen down on the first keystroke - so never trust them here.
    return options.filter(
      (o) => (o.label ?? '').toLowerCase().includes(q) || (o.sublabel ?? '').toLowerCase().includes(q),
    );
  }, [options, query]);

  /*
   * Ticked first when the list is capped, so an earlier choice never sits below the cut. "Ticked" is
   * what was ticked when the list opened or the search last changed - not live: a row ticked now
   * stays where it is, instead of leaping to the top from under the cursor.
   */
  const [pinned, setPinned] = useState<Set<string>>(() => new Set(selected.map(String)));
  useEffect(() => {
    setPinned(new Set(selected.map(String)));
    // Only a new search re-sorts; ticking does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  const shown = useMemo(() => {
    if (!maxRendered || filtered.length <= maxRendered) return filtered;
    const first = filtered.filter((o) => pinned.has(String(o.value)));
    const rest = filtered.filter((o) => !pinned.has(String(o.value)));
    return [...first, ...rest].slice(0, Math.max(maxRendered, first.length));
  }, [filtered, maxRendered, pinned]);
  const hiddenCount = filtered.length - shown.length;

  const toggle = (value: T) => {
    if (selectedSet.has(String(value))) {
      onChange(selected.filter((v) => String(v) !== String(value)));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {searchable && (
        <input
          type="text"
          className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t(searchPlaceholder)}
        />
      )}
      <div className={`overflow-y-auto overflow-x-hidden ${maxHeightClass} border border-(--border-main) rounded-lg`}>
        {loading && options.length === 0 ? (
          <div className="flex items-center gap-2 p-3 body-sm-regular text-(--font-secondary)" role="status">
            <CircularProgress size={16} thickness={5} sx={{ color: 'var(--surface-primary)' }} />
            {t('loading')}
          </div>
        ) : filtered.length === 0 ? (
          <p className="body-sm-regular text-(--font-secondary) p-3">
            {emptyText ?? t('featureFlags.noResults')}
          </p>
        ) : (
          shown.map((o) => (
            <label
              key={String(o.value)}
              className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 body-sm-regular min-w-0"
            >
              <input
                type="checkbox"
                checked={selectedSet.has(String(o.value))}
                onChange={() => toggle(o.value)}
              />
              <span className="flex-1 min-w-0 truncate">{o.label}</span>
              {o.sublabel && (
                <span className="text-(--font-secondary) body-xs-regular shrink-0 max-w-[40%] truncate" dir="ltr">
                  {o.sublabel}
                </span>
              )}
              {renderRowEnd && (
                <span className="shrink-0" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                  {renderRowEnd(o)}
                </span>
              )}
            </label>
          ))
        )}
        {hiddenCount > 0 && (
          <p className="body-xs-regular text-(--font-secondary) px-3 py-2 border-t border-(--border-main)">
            {t('featureFlags.moreToSearch', { shown: shown.length, total: filtered.length.toLocaleString() })}
          </p>
        )}
      </div>
      {!hideCount && (
        <span className="body-xs-regular text-(--font-secondary)">
          {t('featureFlags.selectedCount', { count: selected.length })}
        </span>
      )}
    </div>
  );
}

export default CheckboxMultiSelect;
